/* eslint-disable max-lines */
import { createTheme, Theme } from '@mui/material/styles';

import LatoFont from './fonts/lato-v22-latin-regular.woff2';

const Lato: any = {
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 400,
  src: `
    local('Lato'),
    local('Lato-Regular'),
    url(${LatoFont}) format('woff2')
  `,
};

export const baseTheme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [Lato],
        '*::-webkit-scrollbar': {
          width: '8px',
        },
        '*::-webkit-scrollbar-track': {
          WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#C6C6C6',
          borderRadius: '4px',
        },
        html: {
          height: '100%',
          fontSize: 14,
        },
        body: {
          fontSize: '0.875rem',
          height: '100%',
          margin: 0,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        code: {
          fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
        },
        '.fullPage': {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        },
        '.actionCell': {
          marginRight: '0.5rem',
          cursor: 'pointer',
          '&:last-child': {
            marginRight: 0,
          },
        },
        '.nonPaddedContent': {
          padding: '0 !important',
        },
        '.progress': {
          marginBottom: '0.5rem',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '24px',
        },
        fontSizeSmall: {
          fontSize: '20px',
        },
        fontSizeLarge: {
          fontSize: '35px',
        },
        colorPrimary: {
          color: '#FFFFFF',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          fontWeight: 'bold',
          width: '48px',
          height: '48px',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          paddingBottom: '8px',
          lineHeight: '16px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          display: 'inline-block !important',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          height: 48,
          '& .MuiFormLabel-root': {
            // color: '#000000',
            fontWeight: 700,
          },
          // '& .MuiTableCell-head': {
          //   backgroundColor: '#1e1e1e',
          //   // '& .MuiIconButton-label': {
          //   //   color: '#000000',
          //   // },
          // },
        },
      },
    },
    // MuiTableBody: {
    //   styleOverrides: {
    //     root: {
    //       '& .MuiTableRow-root': {
    //         '&:hover': {
    //           background: '#F2F2F5 !important',
    //           '& .MuiTableCell-root': {
    //             background: '#F2F2F5 !important',
    //           },
    //         },
    //       },
    //     },
    //   },
    // },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          fontSize: '14px !important',
          fontWeight: 700,
          color: '#00000',
          zIndex: 3,
          backgroundColor: theme.palette.background.paper,
        }),
        root: {
          fontSize: 12,
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: { root: { fontSize: '14px !important', fontWeight: 700 } },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: '96px',
          height: '32px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: '#d32f2f',
        },
        outlinedError: {
          backgroundColor: '#d32f2f',
        },
        filledError: {
          backgroundColor: '#d32f2f',
        },
      },
    },
  },
  palette: {
    secondary: {
      main: '#f44336',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
    text: {
      primary: '#121212',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: ['Lato', 'sans-serif'].join(','),
    fontSize: 14,
    caption: {
      fontWeight: 'normal',
      lineHeight: '14px',
      fontSize: '12px',
      textTransform: 'uppercase',
      color: '#FFFFFF',
    },
    subtitle1: {
      lineHeight: '15px',
      fontSize: 12,
    },
    subtitle2: {
      lineHeight: '15px',
      fontSize: 12,
    },
    h5: {
      fontWeight: 700,
      fontSize: 16,
    },
    button: {
      fontWeight: 'bold',
      fontSize: 12,
      lineHeight: '15.6px',
      color: '#FFFFFF',
    },
  },
});

export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#82b1ff',
      dark: '#0d47a1',
      // dark: 'rgb(130, 177, 255)',
      light: '#1976d2',
    },
    secondary: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
      dark: '#e65100',
      light: '#ffb74d',
    },
    error: {
      main: '#f44336',
      dark: '#b71c1c',
      light: '#e57373',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  components: {
    ...baseTheme.components,
  },
});

export const themePROD = createTheme({
  ...baseTheme,
  palette: {
    primary: {
      main: '#047C40',
      dark: '#084426',
      light: 'rgba(0, 0, 220, 0.08)',
      contrastText: '#FFFFFF',
    },
  },
});

export const themeUAT = createTheme({
  ...baseTheme,
  palette: {
    primary: {
      main: '#0000DC',
      dark: '#00008F',
      light: 'rgba(0, 0, 220, 0.08)',
      contrastText: '#FFFFFF',
    },
  },
});

export const themeCI = createTheme({
  ...baseTheme,
  palette: {
    primary: {
      main: '#000042',
      dark: '#00008F',
      light: 'rgba(0, 0, 220, 0.08)',
      contrastText: '#FFFFFF',
    },
  },
  components: {
    ...baseTheme.components,
    MuiLink: {
      styleOverrides: {
        root: { color: '#00008F' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#00008F',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        textColorPrimary: {
          '&.Mui-selected': {
            color: '#00008F',
          },
        },
      },
    },
  },
});

export const themeMap = {
  light: baseTheme,
  dark: darkTheme,
};

export const themeMapping: { [key: string]: Theme } = {
  CI: themeCI,
  UAT: themeUAT,
  PROD: themePROD,
};
